import React, { useRef, useState } from "react";

// Wraps a card so horizontal-swipe-right triggers onSwipeAway (move-to-bottom).
// Vertical scrolling still works because of touchAction: pan-y. Pass
// `disabled` to suppress the gesture while scanning so the user doesn't
// accidentally reorder a card they're framing the camera over.
export default function SwipeableCard({ disabled, onSwipeAway, children }) {
  const [dx, setDx] = useState(0);
  const [animating, setAnimating] = useState(false);
  const startRef = useRef(null);
  const THRESHOLD = 110;

  const reset = (animate) => {
    setAnimating(animate);
    setDx(0);
    if (animate) setTimeout(() => setAnimating(false), 200);
  };

  const onPointerDown = (e) => {
    if (disabled) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    startRef.current = { x: e.clientX, y: e.clientY, locked: false };
    setAnimating(false);
  };

  const onPointerMove = (e) => {
    if (!startRef.current) return;
    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    // Lock direction on the first significant move. If the user is scrolling
    // vertically, abort the swipe so the page scrolls naturally.
    if (!startRef.current.locked) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      if (Math.abs(dy) > Math.abs(dx)) {
        startRef.current = null;
        return;
      }
      startRef.current.locked = true;
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    }
    // Only respond to swipe-right.
    setDx(Math.max(0, dx));
  };

  const onPointerUp = (e) => {
    if (!startRef.current) return;
    const finalDx = Math.max(0, e.clientX - startRef.current.x);
    startRef.current = null;
    if (finalDx > THRESHOLD) {
      setAnimating(true);
      setDx(window.innerWidth);
      setTimeout(() => {
        onSwipeAway?.();
        setDx(0);
        setAnimating(false);
      }, 220);
    } else {
      reset(true);
    }
  };

  const progress = Math.min(1, dx / THRESHOLD);

  return (
    <div
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => { startRef.current = null; reset(true); }}
      style={{
        position: "relative",
        touchAction: "pan-y",
        transform: `translateX(${dx}px)`,
        transition: animating ? "transform 200ms ease-out" : "none",
        opacity: 1 - 0.3 * progress,
      }}
    >
      {dx > 20 && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            paddingLeft: 24,
            color: progress >= 1 ? "#198754" : "#6c757d",
            fontWeight: 600,
            fontSize: 14,
            pointerEvents: "none",
            zIndex: -1,
          }}
        >
          {progress >= 1 ? "↓ Release to move to bottom" : "↓ Move to bottom"}
        </div>
      )}
      {children}
    </div>
  );
}
